import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import AstralChart from './AstralChart.tsx';
import { ProductId, isValidProductId } from '@shared/products';
import Logo from './ui/Logo.tsx';
import { format, parse } from 'date-fns';

interface UserInfo {
  fullName: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  city: string;
  latitude: number;
  longitude: number;
  email: string;
}

const AstralMapPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const purchaseId = searchParams.get('purchaseId');
  const productId = searchParams.get('productId') as ProductId;

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!purchaseId) {
        setError('Purchase ID is required');
        setLoading(false);
        return;
      }

      if (!productId || !isValidProductId(productId)) {
        setError('Valid product ID is required');
        setLoading(false);
        return;
      }

      try {
        // Fetch user info from Firebase Functions
        const getUserInfo = httpsCallable(functions, 'getUserInfo');

        const result = await getUserInfo({ purchaseId });
        const data = result.data as any;

        if (data.success && data.userInfo) {
          // Transform the userInfo to match our interface
          const userInfo: UserInfo = {
            fullName: data.userInfo.fullName,
            birthDate: data.userInfo.birthDate,
            birthTime: data.userInfo.birthTime,
            birthPlace: data.userInfo.birthPlace,
            city: data.userInfo.birthPlace.split(',')[0]?.trim() || data.userInfo.birthPlace,
            latitude: data.userInfo.latitude,
            longitude: data.userInfo.longitude,
            email: data.userInfo.email
          };

          setUserInfo(userInfo);
        } else {
          setError('User information not found');
        }
      } catch (error: any) {
        console.error('Error fetching user info:', error);
        if (error.code === 'functions/not-found') {
          setError('User information not found for this purchase ID');
        } else {
          setError('Failed to fetch user information');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, [purchaseId, productId]);

  if (loading) {
    return (
      <div className="astral-map-page-loading">
        <div className="loading-spinner"></div>
        <p>Loading astral map...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="astral-map-page-error">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div className="astral-map-page-error">
        <h2>Error</h2>
        <p>User information not found</p>
      </div>
    );
  }

  return (
    <div className="astral-map-page bg-white fixed top-0 left-0 right-0 bottom-0 w-full h-full" style={
      {
        background: "#ffcc00 !important"
      }
    }>
      <div className='flex flex-col items-center justify-between p-8 pb-0 gap-10 border-t-[20px] border-purple-700'>
        <Logo />
        <div className='flex items-center border rounded-xl text-purple-800 overflow-hidden'>
          <div className='flex-1'>
            <h1 className='text-xl font-bold w-[280px] p-3'>{userInfo.fullName}</h1>
          </div>
          <div className="text-left border-l border-gray-200 p-3 px-4">
            <p className='text-sm'>{format(parse(userInfo.birthDate, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')} - {userInfo.birthTime}</p>
            <p className='text-sm'>{userInfo.birthPlace}</p>
            <p className='text-sm'>{userInfo.latitude}, {userInfo.longitude}</p>
          </div>
        </div>
      </div>
      <AstralChart
        birthData={userInfo}
        type={productId}
      />
    </div>
  );
};

export default AstralMapPage; 